import type {
  MatchConfig,
  PlacedTower,
  UnitCard,
  GridCoord,
  ActiveUnit,
  Element,
} from '../interfaces/index.js'
import { POINTS_WIN, POINTS_LOSS, POINTS_DRAW, POINTS_SURRENDER } from '../interfaces/index.js'
import { Grid } from './Grid.js'
import { EconomySystem } from './EconomySystem.js'
import { CombatSystem } from './CombatSystem.js'
import { EventBus, createEvent } from './EventBus.js'
import { findPath, type PathGrid, type PathResult } from './Pathfinder.js'

// Pathfinder class wrapper for compatibility with the module interface
class Pathfinder {
  findPath(grid: PathGrid, start: GridCoord, end: GridCoord): PathResult | null {
    return findPath(grid, start, end)
  }
}

export type MatchPhase = 'idle' | 'running' | 'paused' | 'ended'

/**
 * aggressor  – frequent sends, occasional 2-unit bursts, mixed elements
 * strategist – counter-element focus, smart tower upgrades, single sends
 * rusher     – very fast early sends (tier 1), slows after tier 2
 * defender   – rare sends but in large bursts when gold is high
 * swarmer    – slow main timer, always sends 3–5 units per burst
 */
export type BotPersonality = 'aggressor' | 'strategist' | 'rusher' | 'defender' | 'swarmer'

interface BotState {
  playerId: string
  timer: number
  tier: number
  minTier: number         // floor from forcedBotTier — tier never drops below this
  hpMultiplier: number
  intervalMs: number
  upgradeTimer: number
  upgradeCheckTimer: number
  personality: BotPersonality
  burstRemaining: number  // units left to send in the current burst
  burstTimer: number      // countdown between burst sends (ms)
  buildTimer: number      // countdown to next tower placement
}

// Track per-player stats for MatchStats
interface PlayerStats {
  totalDamage: number
  towerDamage: Record<string, number>
  goldEarned: number
  killGold: number
  unitsSent: number
  unitsSentMap: Record<string, number>
  towersBuilt: number
  towersUpgraded: number
  spellsCast: number
}

interface EngineConfig {
  upgradeBaseCosts: Record<number, number>
  demolishRefundRatio: number
  maxTowerLevel: number
  critMultiplier: number
  upgradePerLevelBonus: number
  botConfig: {
    initialIntervalMs: number
    minIntervalMs: number
    intervalDecayFactor: number
    hpEscalationPerMinute: number
    tierChangeIntervalMs: number
    escalationIntervalMs: number
    towerUpgradeIntervalMs: number
    upgradeCheckIntervalMs: number
    baseUpgradeGoldThresholdL2: number
    baseUpgradeGoldThresholdL3: number
    initialTowerCount: number
    botBuildIntervalMs: number
    botTowerCost: number
  }
  spellConfig: {
    fireStormDamage: number
    slowDuration: number
    goldBoostAmount: number
  }
  botBaseUnitConfig: {
    baseHp: number
    baseSpeed: number
    speedPerTier: number
    dodge: number
  }
}

export function defaultEngineConfig(): EngineConfig {
  return {
    upgradeBaseCosts: { 1: 600, 2: 1500 },
    demolishRefundRatio: 0.5,
    maxTowerLevel: 5,
    critMultiplier: 1.6,
    upgradePerLevelBonus: 0.18,
    botConfig: {
      // 20 s gives first-time players enough breathing room to build an initial maze
      // before the first enemy unit arrives (was 15 s — too tight for new users).
      initialIntervalMs: 20000,
      minIntervalMs: 6000,
      intervalDecayFactor: 0.9,
      // +20 % HP/min (was +35 %). Early game is noticeably easier for new players;
      // compounding over 15 min still reaches a challenging late-game ceiling.
      hpEscalationPerMinute: 1.20,
      tierChangeIntervalMs: 300000,
      escalationIntervalMs: 60000,
      towerUpgradeIntervalMs: 30000,
      upgradeCheckIntervalMs: 10000,
      baseUpgradeGoldThresholdL2: 700,
      baseUpgradeGoldThresholdL3: 1800,
      initialTowerCount: 4,
      botBuildIntervalMs: 5000,   // try to place one tower every 5 s
      botTowerCost: 80,           // same as a standard archer tower
    },
    spellConfig: { fireStormDamage: 50, slowDuration: 3000, goldBoostAmount: 100 },
    botBaseUnitConfig: { baseHp: 300, baseSpeed: 2, speedPerTier: 0.2, dodge: 0.02 },
  }
}

export class MatchEngine {
  readonly config: MatchConfig
  readonly eventBus: EventBus
  private grids: Map<string, Grid>
  private economy: EconomySystem
  private combat: CombatSystem
  private pathfinder: Pathfinder
  private paths: Map<string, GridCoord[]>
  private activeUnits: Map<string, ActiveUnit[]>
  private phase: MatchPhase
  private elapsedMs: number
  private botStates: BotState[]
  private unitIdCounter: number
  private nowMs: number
  private playerStats: Map<string, PlayerStats>
  private playerHp: Map<string, number>
  // Track bot minute timer per bot for hp escalation
  private botMinuteAccum: Map<string, number>
  private engineCfg: EngineConfig
  // Counter for generating unique tower instance IDs for bot-placed towers
  private towerInstanceCounter: number

  constructor(
    config: MatchConfig,
    elementMatrix: Record<string, Record<string, number>>,
    engineConfig: EngineConfig = defaultEngineConfig()
  ) {
    this.config = config
    this.engineCfg = engineConfig
    this.eventBus = new EventBus()
    this.grids = new Map()
    this.economy = new EconomySystem(config.players, this.engineCfg.upgradeBaseCosts)
    this.combat = new CombatSystem(elementMatrix)
    this.pathfinder = new Pathfinder()
    this.paths = new Map()
    this.activeUnits = new Map()
    this.phase = 'idle'
    this.elapsedMs = 0
    this.botStates = []
    this.unitIdCounter = 0
    this.towerInstanceCounter = 0
    this.nowMs = 0
    this.playerStats = new Map()
    this.playerHp = new Map()
    this.botMinuteAccum = new Map()
  }

  start(): void {
    this.phase = 'running'
    this.elapsedMs = 0
    this.nowMs = 0

    const map = this.config.map

    for (const player of this.config.players) {
      // Init grid
      const grid = new Grid(map.cols, map.rows)
      grid.init(map)
      this.grids.set(player.id, grid)

      // Init active units
      this.activeUnits.set(player.id, [])

      // Init player HP
      this.playerHp.set(player.id, player.hp)

      // Init stats
      this.playerStats.set(player.id, {
        totalDamage: 0,
        towerDamage: {},
        goldEarned: 0,
        killGold: 0,
        unitsSent: 0,
        unitsSentMap: {},
        towersBuilt: 0,
        towersUpgraded: 0,
        spellsCast: 0,
      })

      // Calculate initial path
      this._recalcPath(player.id)

      // Init bot states (players without a transport are treated as bots;
      // for simplicity we treat any player whose id starts with 'bot' as a bot)
      if (this._isBot(player.id)) {
        const personality = (this.config.forcedBotPersonality as BotPersonality | undefined) ?? this._chooseBotPersonality()
        const minTier = this.config.forcedBotTier ?? 1
        this.botStates.push({
          playerId: player.id,
          timer: this.engineCfg.botConfig.initialIntervalMs,
          tier: minTier,
          minTier,
          hpMultiplier: 1.0,
          intervalMs: this.engineCfg.botConfig.initialIntervalMs,
          upgradeTimer: this.engineCfg.botConfig.towerUpgradeIntervalMs,
          upgradeCheckTimer: this.engineCfg.botConfig.upgradeCheckIntervalMs,
          personality,
          burstRemaining: 0,
          burstTimer: 0,
          buildTimer: 0,  // fire immediately on first tick
        })
        this.botMinuteAccum.set(player.id, 0)
        console.log(`[MatchEngine] Bot personality: ${personality}`)
      }
    }

    // Connect transport and subscribe to incoming events (PvP relay)
    void this.config.transport.connect().catch(() => undefined)
    this.config.transport.onReceive((event) => { this._applyRemoteEvent(event) })
  }

  update(deltaMs: number): void {
    if (this.phase !== 'running') return

    this.elapsedMs += deltaMs
    this.nowMs += deltaMs

    // 2. Tick economy; emit GOLD_CHANGED per player
    const goldResults = this.economy.tick(deltaMs)
    for (const result of goldResults) {
      const stats = this.playerStats.get(result.playerId)
      if (stats !== undefined) {
        stats.goldEarned += result.goldAdded
      }
      this.eventBus.emit(
        createEvent(
          'GOLD_CHANGED',
          result.playerId,
          {
            goldAdded: result.goldAdded,
            newTotal: result.newTotal,
            newIncome: result.newIncome,
          },
          this.nowMs
        )
      )
    }

    // 3. Move units along paths
    this._moveUnits(deltaMs)

    // 4. Run combat
    this._runCombat()

    // 5. Remove dead units, award kill gold, emit UNIT_KILLED
    for (const [playerId, units] of this.activeUnits) {
      // Single-pass partition — avoids running filter twice over the same array.
      const alive: ActiveUnit[] = []
      const dead: ActiveUnit[] = []
      for (const u of units) {
        ;(u.alive ? alive : dead).push(u)
      }
      this.activeUnits.set(playerId, alive)

      for (const unit of dead) {
        // Kill gold goes to the defender (the player whose grid the unit was on)
        // Unit is on playerId's grid but owned by opponent
        const defenderId = playerId
        this.economy.addGold(defenderId, unit.reward)
        const defStats = this.playerStats.get(defenderId)
        if (defStats !== undefined) {
          defStats.killGold += unit.reward
        }

        // Emit GOLD_CHANGED so HUD updates kill-gold immediately
        if (unit.reward > 0) {
          const newTotal = this.economy.getGold(defenderId)
          const newIncome = this.economy.getIncome(defenderId)
          this.eventBus.emit(
            createEvent(
              'GOLD_CHANGED',
              defenderId,
              {
                goldAdded: unit.reward,
                newTotal,
                newIncome,
              },
              this.nowMs
            )
          )
        }

        this.eventBus.emit(
          createEvent(
            'UNIT_KILLED',
            unit.ownerId,
            {
              unitId: unit.id,
              instanceId: unit.id,
              gx: unit.gx,
              gy: unit.gy,
              reward: unit.reward,
              killedOnPlayerId: playerId,
            },
            this.nowMs
          )
        )
      }
    }

    // 6. Handle units at path end: deduct 1 HP from target player.
    //
    // Ghost units (spawned via remote relay) move visually but do NOT deal damage —
    // the sender's client is authoritative and relays an HP_CHANGED event instead.
    // This prevents the desync caused by both clients independently computing base hits
    // at slightly different ticks due to network latency.
    for (const [playerId, units] of this.activeUnits) {
      const reached: ActiveUnit[] = []
      const remaining: ActiveUnit[] = []

      for (const unit of units) {
        const path = unit.path
        if (path.length > 0 && unit.pathIdx >= path.length - 1) {
          reached.push(unit)
        } else {
          remaining.push(unit)
        }
      }

      this.activeUnits.set(playerId, remaining)

      for (const unit of reached) {
        // Ghost units: remove silently and emit a visual-only UNIT_REACHED_BASE.
        // Real HP change comes from the authoritative client via HP_CHANGED relay.
        if (unit.isGhost === true) {
          this.eventBus.emit(
            createEvent(
              'UNIT_REACHED_BASE',
              unit.ownerId,
              {
                unitId: unit.id,
                instanceId: unit.id,
                targetPlayerId: playerId,
                gx: unit.gx,
                gy: unit.gy,
                isGhost: true,
              },
              this.nowMs
            )
          )
          continue
        }

        // Real unit: deduct HP and relay the authoritative value to the opponent.
        const currentHp = this.playerHp.get(playerId) ?? 0
        const newHp = Math.max(0, currentHp - 1)
        this.playerHp.set(playerId, newHp)

        this.eventBus.emit(
          createEvent(
            'UNIT_REACHED_BASE',
            unit.ownerId,
            {
              unitId: unit.id,
              instanceId: unit.id,
              targetPlayerId: playerId,
              gx: unit.gx,
              gy: unit.gy,
            },
            this.nowMs
          )
        )

        const hpChangedEvent = createEvent(
          'HP_CHANGED',
          playerId,
          {
            delta: -1,
            newHp,
            causedByUnit: unit.id,
            ownerId: unit.ownerId,
          },
          this.nowMs
        )
        this.eventBus.emit(hpChangedEvent)

        // The unit's owner is authoritative for this HP change — relay it so
        // the opponent's client can update their view without double-counting.
        if (!this._isBot(unit.ownerId)) {
          this.config.transport.send(hpChangedEvent)
        }

        if (newHp <= 0) {
          this._endMatch('defeat')
          return
        }
      }
    }

    // 7. Tick bot states
    this._tickBot(deltaMs)

    // 8. Check timer
    if (this.elapsedMs >= this.config.durationSecs * 1000) {
      this._endMatch('timeout')
    }
  }

  placeTower(playerId: string, gx: number, gy: number, tower: PlacedTower): boolean {
    // 1. Spend gold
    if (!this.economy.spend(playerId, tower.cost)) return false

    const grid = this.grids.get(playerId)
    if (grid === undefined) {
      this.economy.addGold(playerId, tower.cost)
      return false
    }

    // 2. Place tower on grid
    const placed = grid.placeTower(gx, gy, tower)
    if (!placed) {
      this.economy.addGold(playerId, tower.cost)
      return false
    }

    // 3. Check valid path
    const map = this.config.map
    if (!grid.hasValidPath(map.spawnX, map.spawnY, map.baseX, map.baseY)) {
      // Undo placement and refund
      grid.removeTower(gx, gy)
      this.economy.addGold(playerId, tower.cost)
      return false
    }

    // 4. Recalculate path, emit event
    this._recalcPath(playerId)

    const stats = this.playerStats.get(playerId)
    if (stats !== undefined) {
      stats.towersBuilt += 1
    }

    // Emit GOLD_CHANGED immediately so HUD reflects spent gold without waiting for next tick
    const goldAfterPlace = this.economy.getGold(playerId)
    const incomeAfterPlace = this.economy.getIncome(playerId)
    this.eventBus.emit(createEvent('GOLD_CHANGED', playerId, {
      goldAdded: -tower.cost,
      newTotal: goldAfterPlace,
      newIncome: incomeAfterPlace,
    }, this.nowMs))

    const towerPlacedEvent = createEvent(
      'TOWER_PLACED',
      playerId,
      {
        gx,
        gy,
        towerId: tower.id,
        instanceId: tower.instanceId,
        cost: tower.cost,
        tower: { ...tower },  // full data so the remote side can replicate placement
      },
      this.nowMs
    )
    this.eventBus.emit(towerPlacedEvent)
    if (!this._isBot(playerId)) {
      this.config.transport.send(towerPlacedEvent)
    }

    return true
  }

  upgradeTower(playerId: string, gx: number, gy: number): boolean {
    const grid = this.grids.get(playerId)
    if (grid === undefined) return false

    const tower = grid.getTowerAt(gx, gy)
    if (tower === undefined) return false

    // Max level check via config
    if (tower.level >= this.engineCfg.maxTowerLevel) return false

    const upgradeIndex = tower.level - 1
    const upgrade = tower.upgrades[upgradeIndex]
    if (upgrade === undefined) return false

    if (!this.economy.spend(playerId, upgrade.cost)) return false

    // Apply upgrade
    if (upgrade.dmgBonus !== undefined) {
      tower.dmg += upgrade.dmgBonus
    }
    if (upgrade.spdBonus !== undefined) {
      tower.spd += upgrade.spdBonus
    }
    if (upgrade.incomeBonus !== undefined) {
      this.economy.addIncome(playerId, upgrade.incomeBonus)
    }

    tower.level += 1

    const stats = this.playerStats.get(playerId)
    if (stats !== undefined) {
      stats.towersUpgraded += 1
    }

    const towerUpgradedEvent = createEvent(
      'TOWER_UPGRADED',
      playerId,
      {
        gx,
        gy,
        instanceId: tower.instanceId,
        newLevel: tower.level,
        upgradeCost: upgrade.cost,
        dmgBonus: upgrade.dmgBonus,
        spdBonus: upgrade.spdBonus,
        incomeBonus: upgrade.incomeBonus,
      },
      this.nowMs
    )
    this.eventBus.emit(towerUpgradedEvent)
    if (!this._isBot(playerId)) {
      this.config.transport.send(towerUpgradedEvent)
    }

    return true
  }

  upgradeTowerPath(playerId: string, gx: number, gy: number, path: 'A' | 'B'): boolean {
    const grid = this.grids.get(playerId)
    if (grid === undefined) return false

    const tower = grid.getTowerAt(gx, gy)
    if (tower === undefined) return false

    // Only valid at exactly level 3, and path not yet chosen
    if (tower.level !== 3) return false
    if (tower.path !== null) return false

    tower.path = path

    const pathChosenEvent = createEvent(
      'TOWER_UPGRADED',
      playerId,
      { gx, gy, instanceId: tower.instanceId, newLevel: tower.level, chosenPath: path },
      this.nowMs
    )
    this.eventBus.emit(pathChosenEvent)
    if (!this._isBot(playerId)) {
      this.config.transport.send(pathChosenEvent)
    }

    return true
  }

  demolishTower(playerId: string, gx: number, gy: number): boolean {
    const grid = this.grids.get(playerId)
    if (grid === undefined) return false

    const tower = grid.getTowerAt(gx, gy)
    if (tower === undefined) return false

    const refund = Math.floor(tower.cost * tower.level * this.engineCfg.demolishRefundRatio)

    grid.removeTower(gx, gy)
    this.economy.addGold(playerId, refund)

    // Recalculate path
    this._recalcPath(playerId)

    const demolishEvent = createEvent(
      'TOWER_DEMOLISHED',
      playerId,
      { gx, gy, instanceId: tower.instanceId, refund },
      this.nowMs
    )
    this.eventBus.emit(demolishEvent)
    if (!this._isBot(playerId)) {
      this.config.transport.send(demolishEvent)
    }

    return true
  }

  /**
   * Add gold to a player's economy. Used by coop wave spawner so the virtual
   * wave-bot opponent always has enough gold to send server-driven units.
   */
  addGoldForPlayer(playerId: string, amount: number): void {
    this.economy.addGold(playerId, amount)
  }

  sendUnit(playerId: string, unitCard: UnitCard): boolean {
    if (!this.economy.spend(playerId, unitCard.cost)) return false

    // Find opponent (first other player)
    const opponent = this.config.players.find(p => p.id !== playerId)
    if (opponent === undefined) {
      this.economy.addGold(playerId, unitCard.cost)
      return false
    }

    // Create active unit — each unit gets its own A* path from spawn to base
    const map = this.config.map
    const spawnCoord = { gx: map.spawnX, gy: map.spawnY }
    const unitPath = this._calcUnitPath(opponent.id, spawnCoord.gx, spawnCoord.gy)
      ?? this.paths.get(opponent.id)
      ?? []

    const activeUnit: ActiveUnit = {
      ...unitCard,
      id: String(++this.unitIdCounter),
      curHp: unitCard.hp,
      maxHp: unitCard.hp,
      pathIdx: 0,
      path: unitPath,
      px: spawnCoord.gx,
      py: spawnCoord.gy,
      gx: spawnCoord.gx,
      gy: spawnCoord.gy,
      slowTimer: 0,
      alive: true,
      ownerId: playerId,
    }

    const opponentUnits = this.activeUnits.get(opponent.id) ?? []
    opponentUnits.push(activeUnit)
    this.activeUnits.set(opponent.id, opponentUnits)

    // Increase sender's income
    this.economy.addIncome(playerId, unitCard.income)

    const stats = this.playerStats.get(playerId)
    if (stats !== undefined) {
      stats.unitsSent += 1
      stats.unitsSentMap[unitCard.id] = (stats.unitsSentMap[unitCard.id] ?? 0) + 1
    }

    // Emit GOLD_CHANGED immediately so HUD shows new income rate without waiting for next tick
    const goldAfterSend = this.economy.getGold(playerId)
    const incomeAfterSend = this.economy.getIncome(playerId)
    this.eventBus.emit(createEvent('GOLD_CHANGED', playerId, {
      goldAdded: -unitCard.cost,
      newTotal: goldAfterSend,
      newIncome: incomeAfterSend,
    }, this.nowMs))

    const unitSentEvent = createEvent(
      'UNIT_SENT',
      playerId,
      {
        unitId: unitCard.id,
        instanceId: activeUnit.id,
        targetPlayerId: opponent.id,
        cost: unitCard.cost,
        unitCard: { ...unitCard },  // full card data so the remote side can spawn the unit
      },
      this.nowMs
    )
    this.eventBus.emit(unitSentEvent)
    if (!this._isBot(playerId)) {
      this.config.transport.send(unitSentEvent)
    }

    return true
  }

  castSpell(playerId: string): boolean {
    const player = this.config.players.find(p => p.id === playerId)
    if (player === undefined) return false

    const spell = player.hero.activeSpell
    // Apply basic spell effect based on effect string
    // Basic implementation: damage all enemy units on this player's grid
    const units = this.activeUnits.get(playerId) ?? []
    const spellDamage = this.engineCfg.spellConfig.fireStormDamage

    if (spell.effect.includes('damage')) {
      for (const unit of units) {
        if (unit.alive) {
          unit.curHp -= spellDamage
          unit.alive = unit.curHp > 0
        }
      }
    } else if (spell.effect.includes('slow')) {
      for (const unit of units) {
        if (unit.alive) {
          unit.slowTimer = this.engineCfg.spellConfig.slowDuration
        }
      }
    } else if (spell.effect.includes('gold')) {
      this.economy.addGold(playerId, this.engineCfg.spellConfig.goldBoostAmount)
    }

    const stats = this.playerStats.get(playerId)
    if (stats !== undefined) {
      stats.spellsCast += 1
    }

    const spellEvent = createEvent(
      'SPELL_CAST',
      playerId,
      { spellId: spell.id, spellName: spell.name, effect: spell.effect },
      this.nowMs
    )
    this.eventBus.emit(spellEvent)
    if (!this._isBot(playerId)) {
      this.config.transport.send(spellEvent)
    }

    return true
  }

  upgradeBase(playerId: string): boolean {
    const previousLevel = this.economy.getBaseLevel(playerId)
    const success = this.economy.upgradeBase(playerId)
    if (!success) return false

    const newLevel = this.economy.getBaseLevel(playerId)

    // Apply income bonus: L2 = +50% of base income (×1.5 total), L3 = +100% (×2.0 total)
    // incomePerSec starts at player.incomePerSec; we track base income from initial player config
    const basePlayer = this.config.players.find(p => p.id === playerId)
    const baseIncomePerSec = basePlayer?.incomePerSec ?? 2

    // At L1→L2: income becomes 1.5× base. At L2→L3: income becomes 2.0× base.
    // We add the delta to the existing income.
    if (previousLevel === 1 && newLevel === 2) {
      // +50% of base income
      const incomeBonus = baseIncomePerSec * 0.5
      this.economy.addIncome(playerId, incomeBonus)
    } else if (previousLevel === 2 && newLevel === 3) {
      // From L2 we're at 1.5× base; we need to reach 2.0× base → add another +50% of base
      const incomeBonus = baseIncomePerSec * 0.5
      this.economy.addIncome(playerId, incomeBonus)
    }

    // Apply HP bonus: +5 HP at L2, +10 HP additional at L3
    const hpBonus = newLevel === 2 ? 5 : newLevel === 3 ? 10 : 0
    if (hpBonus > 0) {
      const currentHp = this.playerHp.get(playerId) ?? 0
      const newHp = currentHp + hpBonus
      this.playerHp.set(playerId, newHp)

      this.eventBus.emit(
        createEvent(
          'HP_CHANGED',
          playerId,
          {
            delta: hpBonus,
            newHp,
            causedByBaseUpgrade: true,
          },
          this.nowMs
        )
      )
    }

    // Emit GOLD_CHANGED so HUD reflects updated income
    const newTotal = this.economy.getGold(playerId)
    const newIncome = this.economy.getIncome(playerId)
    this.eventBus.emit(
      createEvent(
        'GOLD_CHANGED',
        playerId,
        {
          goldAdded: 0,
          newTotal,
          newIncome,
        },
        this.nowMs
      )
    )

    const baseUpgradedEvent = createEvent(
      'BASE_UPGRADED',
      playerId,
      {
        newLevel,
        incomePerSec: newIncome,
        hp: this.playerHp.get(playerId) ?? 0,
      },
      this.nowMs
    )
    this.eventBus.emit(baseUpgradedEvent)
    if (!this._isBot(playerId)) {
      this.config.transport.send(baseUpgradedEvent)
    }

    return true
  }

  pause(): void {
    this.phase = 'paused'
  }

  resume(): void {
    this.phase = 'running'
  }

  getPhase(): MatchPhase {
    return this.phase
  }

  getElapsedMs(): number {
    return this.elapsedMs
  }

  getRemainingMs(): number {
    return Math.max(0, this.config.durationSecs * 1000 - this.elapsedMs)
  }

  getGrid(playerId: string): Grid | undefined {
    return this.grids.get(playerId)
  }

  getActiveUnits(playerId: string): ActiveUnit[] {
    return this.activeUnits.get(playerId) ?? []
  }

  getAllUnits(): ActiveUnit[] {
    const result: ActiveUnit[] = []
    for (const units of this.activeUnits.values()) {
      result.push(...units)
    }
    return result
  }

  getPath(playerId: string): GridCoord[] {
    return this.paths.get(playerId) ?? []
  }

  private _moveUnits(deltaMs: number): void {
    for (const [, units] of this.activeUnits) {
      for (const unit of units) {
        if (!unit.alive) continue
        const path = unit.path
        if (path.length === 0) continue

        let effectiveSpd = unit.spd
        if (unit.slowTimer > 0) {
          effectiveSpd = unit.spd * 0.5
          unit.slowTimer = Math.max(0, unit.slowTimer - deltaMs)
        }

        // Advance along path by speed (cells per second)
        unit.pathIdx += effectiveSpd * (deltaMs / 1000)

        // Clamp to end of path
        if (unit.pathIdx >= path.length) {
          unit.pathIdx = path.length - 1
        }

        const coordIdx = Math.floor(unit.pathIdx)
        const coord = path[coordIdx]
        if (coord !== undefined) {
          unit.gx = coord.gx
          unit.gy = coord.gy
          unit.px = coord.gx
          unit.py = coord.gy
        }
      }
    }
  }

  private _runCombat(): void {
    for (const [playerId, grid] of this.grids) {
      // getTowers() is O(n towers) — avoids the old O(cols×rows)=O(280) cell scan.
      const towers = grid.getTowers().filter(t => t.cat === 'attack')

      // Collect alive units without a separate filter allocation.
      const units = this.activeUnits.get(playerId) ?? []
      const aliveUnits: ActiveUnit[] = []
      for (const u of units) {
        if (u.alive) aliveUnits.push(u)
      }

      if (aliveUnits.length === 0) continue

      for (const tower of towers) {
        const inRange = this.combat.getUnitsInRange(tower, aliveUnits)
        if (inRange.length === 0) continue

        // Attack nearest unit
        const nearest = inRange[0]
        if (nearest === undefined) continue

        const result = this.combat.tryAttack(tower, nearest, this.nowMs)
        if (result === null) continue

        if (!result.isDodge && result.damage > 0) {
          const stats = this.playerStats.get(playerId)
          if (stats !== undefined) {
            stats.totalDamage += result.damage
            const towerKey = tower.id
            stats.towerDamage[towerKey] = (stats.towerDamage[towerKey] ?? 0) + result.damage
          }
        }

        // Handle slow effect
        if (tower.slow !== undefined && tower.slow > 0 && nearest.alive) {
          nearest.slowTimer = Math.max(nearest.slowTimer, tower.slow * 1000)
        }

        // Handle splash
        if (tower.splash !== undefined && tower.splash > 0) {
          const splashTargets = this.combat.getUnitsInRange(
            { ...tower, range: tower.splash },
            aliveUnits.filter(u => u !== nearest)
          )
          for (const splashTarget of splashTargets) {
            // Apply 50% splash damage
            const splashDmg = Math.floor(result.rawDamage * 0.5)
            this.combat.applyDamage(splashTarget, splashDmg, result.elementMultiplier, splashTarget.armor)
          }
        }

        // Handle chain
        if (tower.chain !== undefined && tower.chain > 0 && nearest.alive) {
          const chainTargets = this.combat.getChainTargets(nearest, aliveUnits, tower.chain, tower.range)
          for (const chainTarget of chainTargets) {
            this.combat.applyDamage(chainTarget, result.rawDamage * 0.5, result.elementMultiplier, chainTarget.armor)
          }
        }
      }
    }
  }

  private _endMatch(reason: 'defeat' | 'timeout' | 'quit'): void {
    this.phase = 'ended'

    const winner = this._determineWinner()

    // Build aggregate stats (sum across all players)
    let totalDamage = 0
    const towerDamage: Record<string, number> = {}
    let goldEarned = 0
    let killGold = 0
    let unitsSent = 0
    let towersBuilt = 0
    let towersUpgraded = 0
    let spellsCast = 0

    for (const stats of this.playerStats.values()) {
      totalDamage += stats.totalDamage
      goldEarned += stats.goldEarned
      killGold += stats.killGold
      unitsSent += stats.unitsSent
      towersBuilt += stats.towersBuilt
      towersUpgraded += stats.towersUpgraded
      spellsCast += stats.spellsCast
      for (const [k, v] of Object.entries(stats.towerDamage)) {
        towerDamage[k] = (towerDamage[k] ?? 0) + v
      }
    }

    const localPlayerId = this.config.localPlayerId
    const localHp = this.playerHp.get(localPlayerId) ?? 0
    const opponents = this.config.players.filter(p => p.id !== localPlayerId)
    const maxOpponentHp = opponents.reduce((max, p) => Math.max(max, this.playerHp.get(p.id) ?? 0), 0)
    const outcome: 'win' | 'lose' | 'draw' =
      localHp > maxOpponentHp ? 'win' :
      localHp < maxOpponentHp ? 'lose' : 'draw'

    const pointsDelta =
      outcome === 'win' ? POINTS_WIN :
      outcome === 'lose' ? POINTS_LOSS :
      POINTS_DRAW

    // Per-player breakdown — added alongside the aggregate stats
    const playerStats: Record<string, unknown> = {}
    for (const [pid, stats] of this.playerStats) {
      playerStats[pid] = {
        totalDamage: stats.totalDamage,
        towerDamage: stats.towerDamage,
        goldEarned: stats.goldEarned,
        killGold: stats.killGold,
        unitsSent: stats.unitsSent,
        unitsSentMap: stats.unitsSentMap,
        towersBuilt: stats.towersBuilt,
      }
    }

    const result = {
      id: `match-${Date.now()}`,
      timestamp: new Date().toISOString(),
      mode: this.config.mode,
      mapId: this.config.map.id,
      durationSecs: Math.floor(this.elapsedMs / 1000),
      outcome,
      reason,
      winner,
      pointsDelta,
      stats: {
        totalDamage,
        towerDamage,
        goldEarned,
        killGold,
        unitsSent,
        towersBuilt,
        towersUpgraded,
        spellsCast,
        playerStats,
      },
    }

    this.eventBus.emit(
      createEvent(
        'MATCH_END',
        localPlayerId,
        result as unknown as Record<string, unknown>,
        this.nowMs
      )
    )
  }

  private _determineWinner(): string | null {
    // Most HP wins. If tied: most income. If still tied: null.
    let bestPlayerId: string | null = null
    let bestHp = -Infinity
    let bestIncome = -Infinity
    let tied = false

    for (const player of this.config.players) {
      const hp = this.playerHp.get(player.id) ?? 0
      const income = this.economy.getIncome(player.id)

      if (hp > bestHp) {
        bestHp = hp
        bestIncome = income
        bestPlayerId = player.id
        tied = false
      } else if (hp === bestHp) {
        if (income > bestIncome) {
          bestIncome = income
          bestPlayerId = player.id
          tied = false
        } else if (income === bestIncome) {
          tied = true
        }
      }
    }

    return tied ? null : bestPlayerId
  }

  private _tickBot(deltaMs: number): void {
    for (const bot of this.botStates) {
      // ── Minute escalation ─────────────────────────────────────────────────
      const prevAccum = this.botMinuteAccum.get(bot.playerId) ?? 0
      const newAccum = prevAccum + deltaMs
      this.botMinuteAccum.set(bot.playerId, newAccum % this.engineCfg.botConfig.escalationIntervalMs)

      if (newAccum >= this.engineCfg.botConfig.escalationIntervalMs) {
        bot.hpMultiplier *= this.engineCfg.botConfig.hpEscalationPerMinute
        bot.intervalMs = Math.max(
          this.engineCfg.botConfig.minIntervalMs,
          bot.intervalMs * this.engineCfg.botConfig.intervalDecayFactor
        )
      }

      // ── Tier change every tierChangeIntervalMs ────────────────────────────
      const elapsedMinutes = Math.floor(this.elapsedMs / 60000)
      const tierIntervalMinutes = Math.floor(this.engineCfg.botConfig.tierChangeIntervalMs / 60000)
      bot.tier = Math.max(bot.minTier, Math.floor(elapsedMinutes / tierIntervalMinutes) + 1)

      // ── Main send timer: starts a new burst when it fires ─────────────────
      bot.timer -= deltaMs
      if (bot.timer <= 0 && bot.burstRemaining === 0) {
        const mult = this._getBotIntervalMultiplier(bot)
        bot.timer = Math.max(
          this.engineCfg.botConfig.minIntervalMs,
          bot.intervalMs * mult
        )
        bot.burstRemaining = this._getBotBurstSize(bot)
        bot.burstTimer = 0   // first burst unit fires this tick
      }

      // ── Burst handler: send one unit per burst-delay tick ─────────────────
      if (bot.burstRemaining > 0) {
        bot.burstTimer -= deltaMs
        if (bot.burstTimer <= 0) {
          this._botSendUnit(bot)
          bot.burstRemaining--
          if (bot.burstRemaining > 0) {
            bot.burstTimer = this._getBotBurstDelay(bot)
          }
        }
      }

      // ── Tower building (one tower per interval, uses real gold) ──────────
      bot.buildTimer -= deltaMs
      if (bot.buildTimer <= 0) {
        bot.buildTimer = this.engineCfg.botConfig.botBuildIntervalMs
        this._botBuildTower(bot)
      }

      // ── Tower upgrades ────────────────────────────────────────────────────
      bot.upgradeTimer -= deltaMs
      if (bot.upgradeTimer <= 0) {
        bot.upgradeTimer = this._getBotUpgradeIntervalMs(bot)
        this._botUpgradeTower(bot)
      }

      // ── Base upgrades (throttled) ─────────────────────────────────────────
      bot.upgradeCheckTimer -= deltaMs
      if (bot.upgradeCheckTimer <= 0) {
        bot.upgradeCheckTimer = this.engineCfg.botConfig.upgradeCheckIntervalMs
        this._botUpgradeBase(bot)
      }
    }
  }

  private _chooseBotPersonality(): BotPersonality {
    const all: BotPersonality[] = ['aggressor', 'strategist', 'rusher', 'defender', 'swarmer']
    return all[Math.floor(Math.random() * all.length)]!
  }

  /** Multiplier applied to bot.intervalMs each cycle (after the base interval decays). */
  private _getBotIntervalMultiplier(bot: BotState): number {
    const r = Math.random()
    switch (bot.personality) {
      case 'aggressor':  return 0.60 + r * 0.20   // 60–80 % of base interval
      case 'strategist': return 0.90 + r * 0.30   // 90–120 %
      case 'rusher':     return bot.tier <= 1
        ? 0.40 + r * 0.15   // very aggressive in tier 1
        : 1.30 + r * 0.40   // slows down in tier 2+
      case 'defender':   return 2.00 + r * 0.60   // 200–260 %
      case 'swarmer':    return 2.50 + r * 0.80   // 250–330 %
    }
  }

  /** How many units to fire in one burst (first unit fires immediately). */
  private _getBotBurstSize(bot: BotState): number {
    const gold = this.economy.getGold(bot.playerId)
    switch (bot.personality) {
      case 'aggressor':  return Math.random() < 0.35 ? 2 : 1
      case 'strategist': return 1
      case 'rusher':     return 1
      case 'defender':   return gold > 600 ? 2 + Math.floor(Math.random() * 3) : 1
      case 'swarmer':    return 3 + Math.floor(Math.random() * 3)   // 3–5 units
    }
  }

  /** Milliseconds between consecutive units inside a burst. */
  private _getBotBurstDelay(bot: BotState): number {
    switch (bot.personality) {
      case 'swarmer':  return 250 + Math.random() * 200
      case 'defender': return 700 + Math.random() * 500
      default:         return 400 + Math.random() * 300
    }
  }

  /** Tower upgrade check interval per personality. */
  private _getBotUpgradeIntervalMs(bot: BotState): number {
    switch (bot.personality) {
      case 'defender':   return 10000
      case 'strategist': return 15000
      case 'aggressor':  return 20000
      case 'rusher':     return 50000
      case 'swarmer':    return 45000
    }
  }

  /**
   * Element for the next bot unit, driven by personality:
   * - strategist always counters the player's dominant tower element
   * - rusher uses neutral (cheapest)
   * - defender uses high-threat elements (light/shadow)
   * - aggressor/swarmer counter 60 % of the time, random otherwise
   */
  private _getBotPreferredElement(bot: BotState): string {
    const dominantEl = this._getDominantPlayerElement(this.config.localPlayerId)
    switch (bot.personality) {
      case 'strategist':
        return dominantEl !== null ? this._getCounterElement(dominantEl) : 'neutral'
      case 'rusher':
        return 'neutral'
      case 'defender': {
        const powerEls = ['light', 'shadow', 'fire', 'water']
        return powerEls[Math.floor(Math.random() * powerEls.length)]!
      }
      default: {
        if (dominantEl !== null && Math.random() < 0.60) {
          return this._getCounterElement(dominantEl)
        }
        const allEls = ['fire', 'water', 'nature', 'elektro', 'light', 'shadow', 'neutral']
        return allEls[Math.floor(Math.random() * allEls.length)]!
      }
    }
  }

  /**
   * Place one tower on the bot's grid, following the zigzag barrier plan.
   *
   * Called from _tickBot on a fixed timer (botBuildIntervalMs). Spends real
   * gold via this.placeTower() so the bot is subject to the same economy
   * constraints as the human player — no free initial maze.
   *
   * Tactical algorithm (path-adjacent A* simulation):
   * 1. Collect candidate cells adjacent to the current A* path OR adjacent to
   *    any already-placed bot tower.
   * 2. For each candidate, temporarily place a tower on the Grid, re-run A*,
   *    measure the resulting path length, then remove the temporary tower.
   * 3. Score = path extension × 10 + personality bonus + adjacency bonus.
   * 4. Place the highest-scoring candidate via this.placeTower() (economy-safe).
   */
  private _botBuildTower(bot: BotState): void {
    const botPlayer = this.config.players.find(p => p.id === bot.playerId)
    if (botPlayer === undefined) return

    const map = this.config.map
    const deckTowerIds = botPlayer.deck.towers
    if (deckTowerIds.length === 0) return

    const grid = this.grids.get(bot.playerId)
    if (grid === undefined) return

    const botGold = this.economy.getGold(bot.playerId)
    if (botGold < this.engineCfg.botConfig.botTowerCost) return

    const spawn: GridCoord = { gx: map.spawnX, gy: map.spawnY }
    const base:  GridCoord = { gx: map.baseX,  gy: map.baseY  }

    // Current A* path for this bot's field (units traverse this)
    const currentPathResult = this.paths.get(bot.playerId)
    const currentPath: GridCoord[] = currentPathResult ?? []
    const currentLength = currentPath.length

    // Build a set of path cells for O(1) adjacency lookup
    const pathSet = new Set<string>()
    for (const cell of currentPath) pathSet.add(`${cell.gx},${cell.gy}`)

    // Collect candidate positions:
    // cells adjacent to path cells or adjacent to existing towers
    const candidateSet = new Set<string>()
    const dirs = [[-1,0],[1,0],[0,-1],[0,1]]

    const addNeighbors = (gx: number, gy: number) => {
      for (const [dx, dy] of dirs) {
        const nx = gx + dx!
        const ny = gy + dy!
        if (nx < 0 || nx >= map.cols || ny < 0 || ny >= map.rows) continue
        if (nx === map.spawnX && ny === map.spawnY) continue
        if (nx === map.baseX  && ny === map.baseY)  continue
        if (grid.getTowerAt(nx, ny) !== undefined) continue
        const cell = grid.getCell(nx, ny)
        if (cell === null || !cell.walkable) continue
        candidateSet.add(`${nx},${ny}`)
      }
    }

    // Seed candidates from current path cells
    for (const cell of currentPath) addNeighbors(cell.gx, cell.gy)

    // Also seed candidates from existing towers (grow the maze organically)
    for (let gx = 0; gx < map.cols; gx++) {
      for (let gy = 0; gy < map.rows; gy++) {
        if (grid.getTowerAt(gx, gy) !== undefined) addNeighbors(gx, gy)
      }
    }

    // If no candidates yet (empty grid, no path), fall back to cells near spawn
    if (candidateSet.size === 0) {
      for (let gy = 0; gy < map.rows; gy++) {
        for (let gx = 1; gx < Math.min(4, map.cols - 1); gx++) {
          if (gx === map.spawnX && gy === map.spawnY) continue
          if (gx === map.baseX  && gy === map.baseY)  continue
          if (grid.getTowerAt(gx, gy) !== undefined) continue
          const cell = grid.getCell(gx, gy)
          if (cell !== null && cell.walkable) candidateSet.add(`${gx},${gy}`)
        }
      }
    }

    // Build a PathGrid adapter for the bot's grid (used in simulations)
    const makePathGrid = (): PathGrid => ({
      cols: map.cols,
      rows: map.rows,
      isWalkable: (x: number, y: number) => {
        const c = grid.getCell(x, y)
        return c !== null && c.walkable
      },
    })

    // Score each candidate
    let bestKey = ''
    let bestScore = -Infinity

    for (const key of candidateSet) {
      const [gxStr, gyStr] = key.split(',')
      const gx = parseInt(gxStr!, 10)
      const gy = parseInt(gyStr!, 10)

      // Temporarily place a dummy tower to measure path impact
      const dummy = this._makeBotTower('archer', `sim-${gx}-${gy}`, gx, gy)
      grid.placeTower(gx, gy, dummy)

      const simResult = findPath(makePathGrid(), spawn, base)
      const newLength = simResult !== null ? simResult.path.length : -1

      grid.removeTower(gx, gy)

      if (newLength < 0) continue  // would block path — discard

      const pathExtension = newLength - currentLength
      const isAdjacentToPath = pathSet.has(`${gx-1},${gy}`) ||
                               pathSet.has(`${gx+1},${gy}`) ||
                               pathSet.has(`${gx},${gy-1}`) ||
                               pathSet.has(`${gx},${gy+1}`)

      let score = pathExtension * 10
      if (isAdjacentToPath) score += 5

      // Personality biases
      score += this._getBotBuildPositionBonus(bot, gx, gy, pathExtension, map.cols, map.rows)

      if (score > bestScore) {
        bestScore = score
        bestKey = key
      }
    }

    if (bestKey === '') return

    const [gxStr, gyStr] = bestKey.split(',')
    const bx = parseInt(gxStr!, 10)
    const by = parseInt(gyStr!, 10)

    const towerId = this._botSelectTowerForBuilding(bot, deckTowerIds)
    const instanceId = `bot-build-${towerId}-${bx}-${by}-${++this.towerInstanceCounter}`
    const towerDef = this._makeBotTower(towerId, instanceId, bx, by)
    this.placeTower(bot.playerId, bx, by, towerDef)
  }

  /** Pick a tower type from the bot's deck, factoring in personality. */
  private _botSelectTowerForBuilding(bot: BotState, deckTowerIds: string[]): string {
    if (deckTowerIds.length === 0) return 'archer'
    if (bot.personality !== 'strategist') {
      return deckTowerIds[Math.floor(Math.random() * deckTowerIds.length)]!
    }
    // Strategist: look at what element the enemy is running most and pick a counter
    const enemy = this.config.players.find(p => p.id !== bot.playerId)
    if (enemy === undefined) return deckTowerIds[0]!
    const enemyGrid = this.grids.get(enemy.id)
    if (enemyGrid === undefined) return deckTowerIds[0]!

    const elCount: Record<string, number> = {}
    for (let gx = 0; gx < this.config.map.cols; gx++) {
      for (let gy = 0; gy < this.config.map.rows; gy++) {
        const t = enemyGrid.getTowerAt(gx, gy)
        if (t !== undefined) elCount[t.el] = (elCount[t.el] ?? 0) + 1
      }
    }
    const dominantEl = Object.entries(elCount).sort((a, b) => b[1] - a[1])[0]?.[0]
    if (dominantEl === undefined) return deckTowerIds[0]!

    const counterMap: Record<string, string[]> = {
      fire: ['frost', 'water'],
      water: ['lightning', 'elektro'],
      nature: ['firemage', 'fire'],
      elektro: ['frost', 'water'],
      light: ['shadow'],
      shadow: ['lightmage', 'light'],
    }
    const preferredIds = counterMap[dominantEl] ?? []
    for (const pref of preferredIds) {
      if (deckTowerIds.includes(pref)) return pref
    }
    return deckTowerIds[0]!
  }

  /** Personality-specific score modifier for tower placement position. */
  private _getBotBuildPositionBonus(
    bot: BotState,
    gx: number,
    gy: number,
    pathExtension: number,
    cols: number,
    rows: number,
  ): number {
    const midRow = Math.floor(rows / 2)
    switch (bot.personality) {
      case 'aggressor':
        // Place closer to the enemy base (high gx = near base)
        return gx > cols / 2 ? 4 : 0
      case 'rusher':
        // Place near spawn side to intercept early paths
        return gx < cols / 3 ? 4 : 0
      case 'defender':
        // Prefers big path extensions; no positional bias
        return pathExtension > 3 ? 6 : 0
      case 'swarmer':
        // Pack towers near the middle rows to create a funnel
        return Math.abs(gy - midRow) <= 2 ? 3 : 0
      case 'strategist':
        // Favour central columns for maximum path-length leverage
        return (gx > cols * 0.25 && gx < cols * 0.75) ? 4 : 0
      default:
        return 0
    }
  }

  /**
   * Build a PlacedTower object for a bot with stats matching the tower type.
   * Stats mirror the values in towers.json so bot towers behave consistently.
   */
  private _makeBotTower(
    towerId: string,
    instanceId: string,
    gx: number,
    gy: number
  ): PlacedTower {
    type TowerStatDef = {
      dmg: number
      spd: number
      range: number
      crit: number
      splash?: number
      slow?: number
      chain?: number
      el: Element
      projSpeed?: number
    }

    // Try config-provided lookup first (allows data-driven overrides)
    const externalStats = this.config.getTowerStats?.(towerId)
    if (externalStats !== undefined) {
      const def: TowerStatDef = {
        dmg: externalStats.dmg,
        spd: externalStats.spd,
        range: externalStats.range,
        crit: externalStats.crit,
        splash: externalStats.splash,
        slow: externalStats.slow,
        chain: externalStats.chain,
        el: 'neutral',
        projSpeed: externalStats.projSpeed,
      }
      return {
        id: towerId,
        instanceId,
        name: towerId.charAt(0).toUpperCase() + towerId.slice(1),
        el: def.el,
        cat: 'attack',
        cost: this.engineCfg.botConfig.botTowerCost,
        col: def.el,
        gx,
        gy,
        level: 1,
        path: null,
        lastShot: 0,
        dmg: def.dmg,
        spd: def.spd,
        range: def.range,
        crit: def.crit,
        splash: def.splash,
        slow: def.slow,
        chain: def.chain,
        income: 0,
        terrain: undefined,
        projSpeed: def.projSpeed,
        upgrades: [
          { cost: 60,  desc: '+18% Schaden', dmgBonus: 0.18 },
          { cost: 100, desc: '+18% Schaden', dmgBonus: 0.18 },
          { cost: 140, desc: '+18% Schaden, +5% Tempo', dmgBonus: 0.18, spdBonus: 0.05 },
          { cost: 200, desc: '+18% Schaden', dmgBonus: 0.18 },
        ],
      }
    }

    // Fallback hardcoded table — values match towers.json
    const towerStats: Record<string, TowerStatDef> = {
      archer:    { dmg: 14, spd: 1.6,  range: 3.0, crit: 0.10, el: 'neutral',  projSpeed: 500 },
      firemage:  { dmg: 27, spd: 0.75, range: 2.8, crit: 0.13, splash: 1.4, el: 'fire',    projSpeed: 350 },
      frost:     { dmg: 10, spd: 1.1,  range: 2.8, crit: 0.07, slow: 0.42, el: 'water',   projSpeed: 300 },
      lightning: { dmg: 21, spd: 1.3,  range: 3.2, crit: 0.16, chain: 2, el: 'elektro', projSpeed: 700 },
      shadow:    { dmg: 25, spd: 1.0,  range: 3.0, crit: 0.20, el: 'shadow',   projSpeed: 420 },
      goldmine:  { dmg: 0,  spd: 0,    range: 0,   crit: 0,    el: 'neutral',  projSpeed: 0 },
      mudpit:    { dmg: 2,  spd: 1.0,  range: 1.0, crit: 0,    slow: 0.50, el: 'neutral', projSpeed: 0 },
      thorns:    { dmg: 5,  spd: 1.0,  range: 1.0, crit: 0,    el: 'neutral',  projSpeed: 0 },
      wall:      { dmg: 0,  spd: 0,    range: 0,   crit: 0,    el: 'neutral',  projSpeed: 0 },
    }

    const def: TowerStatDef = towerStats[towerId] ?? towerStats['archer']!

    return {
      id: towerId,
      instanceId,
      name: towerId.charAt(0).toUpperCase() + towerId.slice(1),
      el: def.el,
      cat: 'attack',
      cost: this.engineCfg.botConfig.botTowerCost,
      col: def.el,
      gx,
      gy,
      level: 1,
      path: null,
      lastShot: 0,
      dmg: def.dmg,
      spd: def.spd,
      range: def.range,
      crit: def.crit,
      splash: def.splash,
      slow: def.slow,
      chain: def.chain,
      income: 0,
      terrain: undefined,
      projSpeed: def.projSpeed,
      upgrades: [
        { cost: 60,  desc: '+18% Schaden', dmgBonus: 0.18 },
        { cost: 100, desc: '+18% Schaden', dmgBonus: 0.18 },
        { cost: 140, desc: '+18% Schaden, +5% Tempo', dmgBonus: 0.18, spdBonus: 0.05 },
        { cost: 200, desc: '+18% Schaden', dmgBonus: 0.18 },
      ],
    }
  }

  /**
   * Upgrade the lowest-level tower on the bot's grid if gold permits.
   * Prioritises towers that haven't been upgraded yet to spread upgrades evenly.
   */
  private _botUpgradeTower(bot: BotState): void {
    const grid = this.grids.get(bot.playerId)
    if (grid === undefined) return

    const map = this.config.map
    const botGold = this.economy.getGold(bot.playerId)

    // Only upgrade when bot has comfortable spare gold (200 minimum buffer)
    if (botGold < 200) return

    let bestTower: PlacedTower | null = null
    let bestLevel = Infinity

    for (let gy = 0; gy < map.rows; gy++) {
      for (let gx = 0; gx < map.cols; gx++) {
        const tower = grid.getTowerAt(gx, gy)
        if (tower === undefined) continue
        if (tower.level >= this.engineCfg.maxTowerLevel) continue

        const upgradeIndex = tower.level - 1
        const upgrade = tower.upgrades[upgradeIndex]
        if (upgrade === undefined) continue
        if (upgrade.cost > botGold) continue

        // Prefer the lowest-level tower to spread upgrades evenly
        if (tower.level < bestLevel) {
          bestLevel = tower.level
          bestTower = tower
        }
      }
    }

    if (bestTower !== null) {
      this.upgradeTower(bot.playerId, bestTower.gx, bestTower.gy)
    }
  }

  /**
   * Upgrade the bot's base when enough gold has accumulated.
   * Called on a throttled timer (upgradeCheckTimer) rather than every tick.
   */
  private _botUpgradeBase(bot: BotState): void {
    const botGold = this.economy.getGold(bot.playerId)
    const baseLevel = this.economy.getBaseLevel(bot.playerId)

    if (baseLevel === 1 && botGold >= this.engineCfg.botConfig.baseUpgradeGoldThresholdL2) {
      this.upgradeBase(bot.playerId)
    } else if (baseLevel === 2 && botGold >= this.engineCfg.botConfig.baseUpgradeGoldThresholdL3) {
      this.upgradeBase(bot.playerId)
    }
  }

  /**
   * Counts tower elements on a player's grid and returns the most common one.
   * Returns null if no towers are found.
   */
  private _getDominantPlayerElement(playerId: string): string | null {
    const grid = this.grids.get(playerId)
    if (grid === undefined) return null

    const map = this.config.map
    const elementCounts: Record<string, number> = {}

    for (let gy = 0; gy < map.rows; gy++) {
      for (let gx = 0; gx < map.cols; gx++) {
        const tower = grid.getTowerAt(gx, gy)
        if (tower === undefined) continue
        elementCounts[tower.el] = (elementCounts[tower.el] ?? 0) + 1
      }
    }

    let dominant: string | null = null
    let maxCount = 0
    for (const [el, count] of Object.entries(elementCounts)) {
      if (count > maxCount) {
        maxCount = count
        dominant = el
      }
    }

    return dominant
  }

  /**
   * Returns the preferred unit element to counter the player's dominant tower element.
   * Counter logic:
   *   fire    → water
   *   water   → nature
   *   nature  → fire
   *   elektro → nature (water would be suicide vs elektro ×2.0)
   *   light   → shadow
   *   shadow  → light
   *   neutral/mixed → neutral (goblin / cheapest)
   */
  private _getCounterElement(dominantEl: string): string {
    const counterMap: Record<string, string> = {
      fire:    'water',
      water:   'nature',
      nature:  'fire',
      elektro: 'nature',
      light:   'shadow',
      shadow:  'light',
    }
    return counterMap[dominantEl] ?? 'neutral'
  }

  /**
   * Picks the best unit id for the bot to send based on player tower composition
   * and the bot's current economy.
   *
   * Strategy:
   * 1. Analyse local player's grid to find dominant tower element.
   * 2. Choose unit element that counters it.
   * 3. If bot gold > 500 or tier >= 2, prefer a higher-tier unit id from the deck.
   * 4. Otherwise return the first (cheapest) unit id.
   *
   * Returns the chosen unit id string, or null if the deck has no units.
   */
  private _getBotPreferredUnit(bot: BotState): string | null {
    const botPlayer = this.config.players.find(p => p.id === bot.playerId)
    if (botPlayer === undefined) return null

    const unitIds = botPlayer.deck.units
    if (unitIds.length === 0) return null

    const botGold = this.economy.getGold(bot.playerId)
    const useHighTier = botGold > 500 || bot.tier >= 2

    if (useHighTier) {
      const tierIndex = Math.min(bot.tier - 1, unitIds.length - 1)
      return unitIds[tierIndex] ?? unitIds[0] ?? null
    }

    return unitIds[0] ?? null
  }

  private _botSendUnit(bot: BotState): void {
    // Find the bot player
    const botPlayer = this.config.players.find(p => p.id === bot.playerId)
    if (botPlayer === undefined) return

    // Find opponent to send units to
    const opponent = this.config.players.find(p => p.id !== bot.playerId)
    if (opponent === undefined) return

    // Element chosen by personality strategy
    const preferredEl = this._getBotPreferredElement(bot)

    // Select unit id strategically (tier + economy aware)
    const unitId = this._getBotPreferredUnit(bot)
    if (unitId === undefined || unitId === null) return

    // Create a synthetic unit card based on tier and strategic element
    const baseHp = this.engineCfg.botBaseUnitConfig.baseHp * bot.hpMultiplier
    const syntheticUnit: UnitCard = {
      id: unitId,
      name: `Bot Unit T${bot.tier}`,
      el: preferredEl as Element,
      cost: 0, // Bots don't spend gold
      rarity: bot.tier >= 3 ? 'rare' : 'common',
      hp: Math.floor(baseHp),
      armor: 0,
      spd: this.engineCfg.botBaseUnitConfig.baseSpeed + bot.tier * this.engineCfg.botBaseUnitConfig.speedPerTier,
      income: 0,
      dodge: this.engineCfg.botBaseUnitConfig.dodge,
      reward: bot.tier * 20,   // kill gold: 20 / 40 / 60 per tier
    }

    // Add unit directly to opponent's active unit list (bypassing cost)
    const botMap = this.config.map
    const botSpawnCoord = { gx: botMap.spawnX, gy: botMap.spawnY }
    const botUnitPath = this._calcUnitPath(opponent.id, botSpawnCoord.gx, botSpawnCoord.gy)
      ?? this.paths.get(opponent.id)
      ?? []

    const activeUnit: ActiveUnit = {
      ...syntheticUnit,
      id: String(++this.unitIdCounter),
      curHp: syntheticUnit.hp,
      maxHp: syntheticUnit.hp,
      pathIdx: 0,
      path: botUnitPath,
      px: botSpawnCoord.gx,
      py: botSpawnCoord.gy,
      gx: botSpawnCoord.gx,
      gy: botSpawnCoord.gy,
      slowTimer: 0,
      alive: true,
      ownerId: bot.playerId,
    }

    const opponentUnits = this.activeUnits.get(opponent.id) ?? []
    opponentUnits.push(activeUnit)
    this.activeUnits.set(opponent.id, opponentUnits)

    this.eventBus.emit(
      createEvent(
        'UNIT_SENT',
        bot.playerId,
        {
          unitId: syntheticUnit.id,
          instanceId: activeUnit.id,
          targetPlayerId: opponent.id,
          tier: bot.tier,
          isBot: true,
        },
        this.nowMs
      )
    )
  }

  private _recalcPath(playerId: string): void {
    const grid = this.grids.get(playerId)
    if (grid === undefined) return

    const map = this.config.map
    const pathGrid = {
      cols: map.cols,
      rows: map.rows,
      isWalkable: (x: number, y: number): boolean => {
        const cell = grid.getCell(x, y)
        return cell !== null && cell.walkable
      },
    }

    const result = this.pathfinder.findPath(
      pathGrid,
      { gx: map.spawnX, gy: map.spawnY },
      { gx: map.baseX, gy: map.baseY }
    )

    if (result !== null) {
      this.paths.set(playerId, result.path)
      // Recalculate every live unit's individual A* path from its current cell.
      // Units that are already past a newly-placed tower find their own detour
      // without being forced onto the global path.
      this._recalcUnitPaths(playerId)
    } else {
      // Fallback: keep previous path or empty
      if (!this.paths.has(playerId)) {
        this.paths.set(playerId, [])
      }
    }
  }

  /** Recalculate the individual A* path for every live unit on the given grid.
   *  Called after tower placement/demolition changes grid walkability. */
  private _recalcUnitPaths(playerId: string): void {
    const units = this.activeUnits.get(playerId) ?? []
    for (const unit of units) {
      if (!unit.alive) continue
      const newPath = this._calcUnitPath(playerId, unit.gx, unit.gy)
      if (newPath !== null && newPath.length > 0) {
        unit.path = newPath
        unit.pathIdx = 0
      }
      // If no path from current cell (very edge case), the unit keeps its old
      // path and continues — it will be caught by the base-reach check.
    }
  }

  /** Run A* for a single unit from (fromGx, fromGy) to the map base.
   *  Returns the path, or null if no route exists. */
  private _calcUnitPath(gridOwnerId: string, fromGx: number, fromGy: number): GridCoord[] | null {
    const grid = this.grids.get(gridOwnerId)
    if (grid === undefined) return null
    const map = this.config.map
    const pathGrid = {
      cols: map.cols,
      rows: map.rows,
      isWalkable: (x: number, y: number): boolean => {
        const cell = grid.getCell(x, y)
        return cell !== null && cell.walkable
      },
    }
    const result = this.pathfinder.findPath(
      pathGrid,
      { gx: fromGx, gy: fromGy },
      { gx: map.baseX, gy: map.baseY }
    )
    return result !== null ? result.path : null
  }

  private _isBot(playerId: string): boolean {
    const player = this.config.players.find(p => p.id === playerId)
    return player?.isBot === true
  }

  /**
   * Apply a GAME_EVENT received via transport from the remote opponent.
   * Called by the transport.onReceive callback — never re-relays events (no transport.send here).
   * Bypasses gold checks so we don't double-spend on the receiving side.
   */
  private _applyRemoteEvent(event: import('../interfaces/index.js').GameEvent): void {
    if (this.phase !== 'running') return
    const p = event.payload as Record<string, unknown>
    const senderId = event.playerId  // the remote player who performed the action

    switch (event.type) {
      case 'TOWER_PLACED': {
        const tower = p['tower'] as PlacedTower | undefined
        const gx = p['gx'] as number
        const gy = p['gy'] as number
        if (tower === undefined) break
        const grid = this.grids.get(senderId)
        if (grid === undefined) break
        // Place directly — skip gold check (gold tracked independently per client)
        const placed = grid.placeTower(gx, gy, { ...tower })
        if (placed) {
          const map = this.config.map
          if (grid.hasValidPath(map.spawnX, map.spawnY, map.baseX, map.baseY)) {
            this._recalcPath(senderId)
          } else {
            grid.removeTower(gx, gy)  // safety: shouldn't happen if clients agree on map
          }
        }
        break
      }

      case 'TOWER_UPGRADED': {
        const gx = p['gx'] as number
        const gy = p['gy'] as number
        const chosenPath = p['chosenPath'] as 'A' | 'B' | undefined
        const grid = this.grids.get(senderId)
        if (grid === undefined) break
        const tower = grid.getTowerAt(gx, gy)
        if (tower === undefined) break

        if (chosenPath !== undefined) {
          // Path-choice upgrade (level 3 branch)
          tower.path = chosenPath
        } else {
          // Stat upgrade — apply bonuses directly
          const dmgBonus = p['dmgBonus'] as number | undefined
          const spdBonus = p['spdBonus'] as number | undefined
          const incomeBonus = p['incomeBonus'] as number | undefined
          if (dmgBonus !== undefined) tower.dmg += dmgBonus
          if (spdBonus !== undefined) tower.spd += spdBonus
          if (incomeBonus !== undefined) this.economy.addIncome(senderId, incomeBonus)
          tower.level += 1
        }
        break
      }

      case 'TOWER_DEMOLISHED': {
        const gx = p['gx'] as number
        const gy = p['gy'] as number
        const grid = this.grids.get(senderId)
        if (grid === undefined) break
        grid.removeTower(gx, gy)
        this._recalcPath(senderId)
        break
      }

      case 'UNIT_SENT': {
        const unitCard = p['unitCard'] as UnitCard | undefined
        const targetPlayerId = p['targetPlayerId'] as string | undefined
        if (unitCard === undefined || targetPlayerId === undefined) break
        const map = this.config.map
        const unitPath = this._calcUnitPath(targetPlayerId, map.spawnX, map.spawnY)
          ?? this.paths.get(targetPlayerId)
          ?? []
        const activeUnit: ActiveUnit = {
          ...unitCard,
          id: String(++this.unitIdCounter),
          curHp: unitCard.hp,
          maxHp: unitCard.hp,
          pathIdx: 0,
          path: unitPath,
          px: map.spawnX,
          py: map.spawnY,
          gx: map.spawnX,
          gy: map.spawnY,
          slowTimer: 0,
          alive: true,
          ownerId: senderId,
          // Ghost: moves visually but the sender's client is authoritative for HP damage.
          isGhost: true,
        }
        const units = this.activeUnits.get(targetPlayerId) ?? []
        units.push(activeUnit)
        this.activeUnits.set(targetPlayerId, units)
        break
      }

      case 'HP_CHANGED': {
        // Authoritative HP update from the sender's client (unit reached base there).
        // Apply directly without touching economy or re-relaying.
        const targetPlayerId = event.playerId
        const newHp = p['newHp'] as number | undefined
        if (newHp === undefined) break
        this.playerHp.set(targetPlayerId, newHp)
        // Re-emit on the local EventBus so useGameStore / UnitRenderer update.
        this.eventBus.emit(
          createEvent(
            'HP_CHANGED',
            targetPlayerId,
            { delta: p['delta'] as number ?? -1, newHp, fromRelay: true },
            this.nowMs
          )
        )
        // Trigger end-match if this player's HP hit zero.
        if (newHp <= 0 && this.phase === 'running') {
          this._endMatch('defeat')
        }
        break
      }

      case 'SPELL_CAST': {
        // Opponent's spell targets units on the LOCAL player's grid
        const effect = p['effect'] as string | undefined
        if (effect === undefined) break
        const localPlayer = this.config.players.find(pl => pl.id !== senderId)
        if (localPlayer === undefined) break
        const spellDamage = this.engineCfg.spellConfig.fireStormDamage
        const units = this.activeUnits.get(localPlayer.id) ?? []
        if (effect.includes('damage')) {
          for (const unit of units) {
            if (unit.alive) { unit.curHp -= spellDamage; unit.alive = unit.curHp > 0 }
          }
        } else if (effect.includes('slow')) {
          for (const unit of units) {
            if (unit.alive) unit.slowTimer = this.engineCfg.spellConfig.slowDuration
          }
        }
        break
      }

      default:
        break
    }
  }

  /**
   * Surrender: the surrendering player immediately loses the match.
   * Emits MATCH_END with reason='quit'.
   */
  surrenderPlayer(playerId: string): void {
    if (this.phase !== 'running') return

    const opponent = this.config.players.find(p => p.id !== playerId)
    if (opponent === undefined) return

    this.phase = 'ended'

    const localPlayerId = this.config.localPlayerId
    const opponentHp = this.playerHp.get(opponent.id) ?? 0
    const surrenderHp = this.playerHp.get(playerId) ?? 0

    // From the local player's perspective: surrendering = lose, opponent = win
    const outcome: 'win' | 'lose' | 'draw' =
      playerId === localPlayerId ? 'lose' :
      opponent.id === localPlayerId ? 'win' : 'lose'

    // Build minimal stats
    let totalDamage = 0
    let goldEarned = 0
    let unitsSent = 0
    let towersBuilt = 0
    for (const stats of this.playerStats.values()) {
      totalDamage += stats.totalDamage
      goldEarned += stats.goldEarned
      unitsSent += stats.unitsSent
      towersBuilt += stats.towersBuilt
    }

    const pointsDelta = playerId === localPlayerId ? POINTS_SURRENDER : POINTS_WIN

    this.eventBus.emit(
      createEvent(
        'MATCH_END',
        localPlayerId,
        {
          outcome,
          reason: 'quit',
          winner: opponent.id,
          loser: playerId,
          winnerName: opponent.name,
          winnerHp: opponentHp,
          loserHp: surrenderHp,
          durationSecs: Math.floor(this.elapsedMs / 1000),
          pointsDelta,
          stats: {
            totalDamage,
            towerDamage: {},
            goldEarned,
            killGold: 0,
            unitsSent,
            towersBuilt,
            towersUpgraded: 0,
            spellsCast: 0,
          },
        } as unknown as Record<string, unknown>,
        this.nowMs
      )
    )
  }

  // Public aliases for private methods (as specified in interface)
  moveUnits(deltaMs: number): void {
    this._moveUnits(deltaMs)
  }

  runCombat(): void {
    this._runCombat()
  }

  endMatch(reason: 'defeat' | 'timeout' | 'quit'): void {
    this._endMatch(reason)
  }

  determineWinner(): string | null {
    return this._determineWinner()
  }

  tickBot(deltaMs: number): void {
    this._tickBot(deltaMs)
  }
}
